import { useState, useEffect, useMemo, useCallback } from "react";
import { t } from "ttag";
import { 
  Button, 
  Input, 
  Select, 
  Switch, 
  Title, 
  Text, 
  Alert, 
  Card, 
  Group, 
  Badge,
  Modal,
  Stack,
  Divider,
  Icon,
} from "metabase/ui";
import { useDispatch } from "metabase/lib/redux";
import { addUndo } from "metabase/redux/undo";

interface UserRestriction {
  id?: number;
  user_id: number;
  user_name?: string;
  user_email?: string;
  allowed_ips: string;
  is_enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  common_name: string;
  is_superuser: boolean;
}

type ModalType = null | "edit" | "delete";

export function UserIpRestrictions() {
  const [restrictions, setRestrictions] = useState<UserRestriction[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  
  // For user search in Select
  const [userSearchQuery, setUserSearchQuery] = useState("");
  
  // For creating new restriction
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [allowedIps, setAllowedIps] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  
  // For modals
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [activeRestriction, setActiveRestriction] = useState<UserRestriction | null>(null);
  const [editAllowedIps, setEditAllowedIps] = useState("");
  const [editIsEnabled, setEditIsEnabled] = useState(true);
  
  const dispatch = useDispatch();

  useEffect(() => {
    fetchRestrictions();
    fetchUsers();
  }, []);

  const fetchRestrictions = async () => {
    try {
      const response = await fetch("/api/user-ip-restriction");
      const data = await response.json();
      setRestrictions(data.restrictions || []);
    } catch (error) {
      console.error("Failed to fetch restrictions:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch("/api/user");
      const data = await response.json();
      // Filter out superusers (they bypass restrictions anyway)
      const nonSuperUsers = (data.data || []).filter((u: User) => !u.is_superuser);
      setUsers(nonSuperUsers);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleSave = async () => {
    if (!selectedUser || !allowedIps) {
      dispatch(
        addUndo({
          message: t`Please select a user and enter allowed IPs`,
          icon: "warning",
          toastColor: "error",
        }),
      );
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/user-ip-restriction/${selectedUser}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          allowed_ips: allowedIps,
          is_enabled: isEnabled,
        }),
      });

      if (response.ok) {
        dispatch(
          addUndo({
            message: t`IP restriction saved successfully`,
            icon: "check",
          }),
        );
        fetchRestrictions();
        setSelectedUser("");
        setAllowedIps("");
        setIsEnabled(true);
      } else {
        const error = await response.json();
        throw new Error(error.message || "Failed to save");
      }
    } catch (error: any) {
      dispatch(
        addUndo({
          message: error.message || t`Failed to save IP restriction`,
          icon: "warning",
          toastColor: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!activeRestriction || !editAllowedIps) {
      dispatch(
        addUndo({
          message: t`Please enter allowed IPs`,
          icon: "warning",
          toastColor: "error",
        }),
      );
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/user-ip-restriction/${activeRestriction.user_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          allowed_ips: editAllowedIps,
          is_enabled: editIsEnabled,
        }),
      });

      if (response.ok) {
        dispatch(
          addUndo({
            message: t`IP restriction updated successfully`,
            icon: "check",
          }),
        );
        fetchRestrictions();
        setActiveModal(null);
        setActiveRestriction(null);
      } else {
        const error = await response.json();
        throw new Error(error.message || "Failed to update");
      }
    } catch (error: any) {
      dispatch(
        addUndo({
          message: error.message || t`Failed to update IP restriction`,
          icon: "warning",
          toastColor: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = useCallback(async () => {
    if (!activeRestriction) {
      return;
    }

    try {
      const response = await fetch(`/api/user-ip-restriction/${activeRestriction.user_id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        dispatch(
          addUndo({
            message: t`IP restriction removed`,
            icon: "check",
          }),
        );
        fetchRestrictions();
        setActiveModal(null);
        setActiveRestriction(null);
      } else {
        throw new Error("Failed to delete");
      }
    } catch (error) {
      dispatch(
        addUndo({
          message: t`Failed to remove IP restriction`,
          icon: "warning",
          toastColor: "error",
        }),
      );
    }
  }, [activeRestriction, dispatch]);

  const openEditModal = (restriction: UserRestriction) => {
    setActiveRestriction(restriction);
    setEditAllowedIps(restriction.allowed_ips);
    setEditIsEnabled(restriction.is_enabled);
    setActiveModal("edit");
  };

  const openDeleteModal = (restriction: UserRestriction) => {
    setActiveRestriction(restriction);
    setActiveModal("delete");
  };

  const closeModal = () => {
    setActiveModal(null);
    setActiveRestriction(null);
    setEditAllowedIps("");
    setEditIsEnabled(true);
  };

  const getUserName = (userId: number) => {
    const user = users.find((u) => u.id === userId);
    return user ? `${user.common_name} (${user.email})` : `User ${userId}`;
  };

  // Filter users for Select search
  const selectUserData = useMemo(() => {
    const unrestrictedUsers = users.filter(
      (user) => !restrictions.some((r) => r.user_id === user.id)
    );
    
    // Filter by search query
    if (userSearchQuery) {
      const query = userSearchQuery.toLowerCase();
      return unrestrictedUsers.filter(user =>
        user.common_name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
      );
    }
    
    return unrestrictedUsers;
  }, [users, restrictions, userSearchQuery]);

  return (
    <div style={{ padding: "2rem", maxWidth: "1200px" }}>
      <Title order={2}>{t`User IP Restrictions`}</Title>
      <Text c="dimmed" mb="lg">
        {t`Restrict login access to specific IP addresses for individual users. Superusers are exempt.`}
      </Text>

      <Alert mb="lg" color="info">
        {t`Format: single IP (192.168.1.100) or CIDR (10.0.0.0/8). Multiple IPs can be comma-separated. Example: 192.168.1.100, 10.0.0.0/8, 127.0.0.1`}
      </Alert>

      {/* Create New Restriction */}
      <Card withBorder mb="xl" p="lg">
        <Title order={4} mb="md">
          {t`Add New Restriction`}
        </Title>

        <Stack gap="md">
          {/* Searchable User Select */}
          <div>
            <Text size="sm" fw={500} mb="xs">{t`Select User`}</Text>
            <Select
              placeholder={t`Search and choose a user...`}
              value={selectedUser}
              onChange={(value) => setSelectedUser(value || "")}
              data={selectUserData.map((user) => ({
                value: String(user.id),
                label: `${user.common_name} (${user.email})`,
              }))}
              searchable
              searchValue={userSearchQuery}
              onSearchChange={setUserSearchQuery}
              nothingFoundMessage={t`No users found`}
              comboboxProps={{ withinPortal: false }}
            />
          </div>

          <Input.Wrapper label={t`Allowed IPs`}>
            <Input
              placeholder="10.0.0.0/8, 192.168.1.100, 127.0.0.1"
              value={allowedIps}
              onChange={(e) => setAllowedIps(e.target.value)}
            />
          </Input.Wrapper>

          <Switch
            label={t`Enabled`}
            checked={isEnabled}
            onChange={(e) => setIsEnabled(e.currentTarget.checked)}
          />

          <Group justify="flex-end">
            <Button 
              onClick={handleSave} 
              loading={loading} 
              disabled={!selectedUser || !allowedIps}
              leftSection={<Icon name="add" size={16} />}
            >
              {t`Save Restriction`}
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Active Restrictions List */}
      <Title order={4} mb="md">
        {t`Active Restrictions`}
        <Badge ml="sm" variant="light">{restrictions.length}</Badge>
      </Title>

      {restrictions.length === 0 ? (
        <Alert variant="light">
          {t`No IP restrictions configured yet.`}
        </Alert>
      ) : (
        <Stack gap="md">
          {restrictions.map((restriction) => (
            <Card key={restriction.id} withBorder p="md">
              <Group justify="space-between" align="flex-start">
                <div style={{ flex: 1 }}>
                  <Group gap="sm" mb="xs">
                    <Text fw={600}>
                      {getUserName(restriction.user_id)}
                    </Text>
                    {restriction.is_enabled ? (
                      <Badge color="green" size="sm">{t`Enabled`}</Badge>
                    ) : (
                      <Badge color="gray" size="sm">{t`Disabled`}</Badge>
                    )}
                  </Group>
                  
                  <Group gap="xs" mb="xs">
                    <Text size="sm" c="dimmed">{t`Allowed IPs:`}</Text>
                    <code style={{ 
                      background: "#f1f3f5", 
                      padding: "4px 8px", 
                      borderRadius: "4px", 
                      fontSize: "0.85em",
                      wordBreak: "break-all"
                    }}>
                      {restriction.allowed_ips}
                    </code>
                  </Group>

                  {restriction.updated_at && (
                    <Text size="xs" c="dimmed">
                      {t`Last updated:`} {new Date(restriction.updated_at).toLocaleString()}
                    </Text>
                  )}
                </div>

                <Group gap="sm">
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    leftSection={<Icon name="pencil" size={16} />}
                    onClick={() => openEditModal(restriction)}
                  >
                    {t`Edit`}
                  </Button>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    color="error"
                    leftSection={<Icon name="trash" size={16} />}
                    onClick={() => openDeleteModal(restriction)}
                  >
                    {t`Delete`}
                  </Button>
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>
      )}

      {/* Edit Modal */}
      <Modal
        opened={activeModal === "edit"}
        onClose={closeModal}
        title={t`Edit IP Restriction`}
        size="lg"
      >
        {activeRestriction && (
          <Stack gap="md">
            <Text>
              {t`User:`} <strong>{getUserName(activeRestriction.user_id)}</strong>
            </Text>

            <Divider />

            <Input.Wrapper label={t`Allowed IPs`} required>
              <Input
                placeholder="10.0.0.0/8, 192.168.1.100, 127.0.0.1"
                value={editAllowedIps}
                onChange={(e) => setEditAllowedIps(e.target.value)}
              />
            </Input.Wrapper>

            <Switch
              label={t`Enabled`}
              checked={editIsEnabled}
              onChange={(e) => setEditIsEnabled(e.currentTarget.checked)}
            />

            <Alert color="info" variant="light">
              {t`Current allowed IPs:`} <code>{activeRestriction.allowed_ips}</code>
            </Alert>

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeModal}>
                {t`Cancel`}
              </Button>
              <Button 
                onClick={handleUpdate} 
                loading={loading}
                disabled={!editAllowedIps}
                leftSection={<Icon name="check" size={16} />}
              >
                {t`Update Restriction`}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={activeModal === "delete"}
        onClose={closeModal}
        title={t`Delete IP Restriction`}
        size="md"
      >
        {activeRestriction && (
          <Stack gap="lg">
            <Text>
              {t`Are you sure you want to remove IP restrictions for `}
              <strong>{getUserName(activeRestriction.user_id)}</strong>?
            </Text>
            
            <Text size="sm" c="dimmed">
              {t`This user will be able to log in from any IP address.`}
            </Text>

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeModal}>
                {t`Cancel`}
              </Button>
              <Button 
                onClick={handleDelete} 
                loading={loading}
                color="error"
                leftSection={<Icon name="trash" size={16} />}
              >
                {t`Delete Restriction`}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
}
